import type { PlanDetailsCopy } from '../planDetails'

const fr: PlanDetailsCopy = {
  fieldLabels: {
    current_age: 'Votre âge',
    retirement_age: 'Âge de départ à la retraite',
    annual_income: 'Revenu annuel',
    annual_savings: 'Épargne annuelle',
    inflation: 'Taux d’inflation',
    realization_tax_rate: 'Taux d’imposition des plus-values',
    cost_basis: 'Prix de revient',
    w0: 'Patrimoine investissable aujourd’hui',
    primary_residence: 'Votre résidence principale',
    total_investable: 'Patrimoine investissable',
    expected_return: 'Rendement espéré',
    volatility: 'Volatilité',
    horizon: 'Horizon de placement',
    jurisdiction: 'Résidence fiscale',
    risk_aversion: 'Tolérance au risque',
    birth_year: 'Année de naissance',
    birth_date: 'Date de naissance',
    marital_status: 'Situation familiale',
    dependents: 'Personnes à charge',
    dependent_ages: 'Âges des personnes à charge',
    children: 'Enfants',
    children_birth_years: 'Années de naissance des enfants',
    partner_age: 'Âge de votre conjoint',
    partner_annual_income: 'Revenu annuel de votre conjoint',
    partner_retirement_age: 'Âge de départ à la retraite de votre conjoint',
    plan_to_age: 'Âge de fin du plan',
    gross_annual_income: 'Revenu annuel brut',
    net_income: 'Revenu net',
    annual_spending: 'Dépenses annuelles',
    essential_spending: 'Dépenses essentielles',
    flexible_spending: 'Dépenses modulables',
    investments_total: 'Total investi',
    total_investable_wealth: 'Patrimoine investissable total',
    portfolio_mix: 'Allocation du portefeuille',
    cgt_rate: 'Taux d’imposition des plus-values mobilières',
    interest_rate: 'Taux d’intérêt',
    dividend_yield: 'Rendement du dividende',
    wealth_tax_rate: 'Taux de l’impôt sur la fortune',
    filing_jurisdictions: 'Pays de déclaration',
    citizenship: 'Nationalité',
    tax_residency: 'Résidence fiscale déclarée',
    state: 'État ou province',
    region: 'Région administrative',
    city: 'Ville',
    locality: 'Commune',
    church_tax_applicable: 'Assujetti à l’impôt ecclésiastique',
    life_cover: 'Assurance décès',
    income_protection: 'Prévoyance (maintien de revenu)',
    employer_match: 'Abondement de l’employeur',
    pension: 'Retraite',
    state_pension: 'Retraite de base',
    social_security: 'Sécurité sociale',
    wage_growth: 'Progression salariale',
    stability: 'Stabilité des revenus',
    has_will: 'Testament rédigé',
    risk_attitude: 'Attitude face au risque',
  },
  keyLabels: {
    goal_priority: 'Ordre des objectifs',
    current_age: 'Âge',
    inflation: 'Hypothèse d’inflation',
    jurisdiction: 'Résidence fiscale',
    realization_tax_rate: 'Impôt dû à la vente pour financer un objectif',
    citizenship: 'Nationalité',
    total_investable: 'Patrimoine investissable',
    annual_income: 'Revenu annuel',
    annual_savings: 'Épargne annuelle',
    risk_tier: 'Niveau de risque',
    retirement_age: 'Âge de départ à la retraite',
    target_wealth: 'Patrimoine cible',
    primary_residence_sellable: 'Vente du logement / passage à un logement plus petit',
  },
  channelLabels: {
    web_levers: 'Leviers sur le site',
    web_cockpit: 'Tableau de décision',
    web: 'Site web',
    chat: 'Assistant IA',
    mcp: 'Assistant IA',
    full_review: 'Revue complète',
    full_plan: 'Plan complet',
    what_if: 'Simulation de scénarios',
  },
  objectiveLabels: {
    status_quo: 'Situation actuelle',
    max_goal_probability: 'Probabilité de succès maximale',
    lowest_drawdown: 'Perte maximale la plus faible',
    highest_growth: 'Croissance la plus forte',
    fastest_to_goal: 'Objectif atteint au plus tôt',
  },
  choices: {
    interest_only: 'In fine (intérêts seuls)',
    fixed_rate_amortizing: 'Amortissable (taux fixe)',
    variable_rate_amortizing: 'Amortissable (taux variable)',
    repayment: 'Amortissable',
    refinance: 'Refinancer au taux du marché',
    repay: 'Rembourser en totalité',
  },
  values: {
    yearsShort: (n) => `${n} ans`,
    shareAbout: (pct) => `environ ${pct} de sa valeur`,
    sharePart: 'une partie de sa valeur',
    aroundAge: (age) => ` vers ${age} ans`,
    inAboutYears: (n) => ` dans environ ${n} ans`,
    maySellFor: (share, when) => `Peut être vendu pour libérer ${share}${when}, uniquement si cela améliore le plan.`,
    maySell: 'Peut être vendu si cela améliore le plan.',
    structuredFact: 'Donnée structurée',
  },
  ask: { talkThrough: 'En parler avec MyCIO' },
  review: {
    openInputCheckFailed: 'La vérification des données manquantes n’a pas pu aboutir. Consultez l’état de la revue ci-dessous.',
    finishedLoading: 'Votre revue est terminée. Chargement des résultats…',
    draftReady: 'Votre revue provisoire est prête : l’analyse réalisée, ce qui n’a pas pu être calculé et les questions qui permettraient de la compléter figurent ci-dessous.',
    finishedNotLoaded: 'Votre revue est terminée, mais ses résultats n’ont pas encore pu être chargés. Nouvel essai en cours — le calcul ne sera pas relancé.',
    fetchFailed: 'Votre revue est terminée, mais nous n’avons pas pu la récupérer pour l’instant. Elle est enregistrée : réessayez dans un instant ; aucun nouveau calcul n’est nécessaire.',
    notComputed: (list) => `Non calculé lors de cette exécution : ${list}.`,
    changesNotSaved: (n, f) => `${n === 1 ? '1 modification n’a pas été enregistrée' : `${f} modifications n’ont pas été enregistrées`} — voir Vos informations.`,
  },
  facts: {
    chooseOption: 'Choisissez une option.',
    couldNotSave: 'Impossible d’enregistrer cette valeur. Votre plan est inchangé.',
    couldNotUndo: 'Impossible d’annuler cette modification.',
    choosePlaceholder: 'Choisir…',
    confirmedBadge: 'Confirmé — demandez la modification dans le chat',
    confirmedTitle: (was) => `Vous avez déjà confirmé cette donnée à ${was}. Demandez à MyCIO dans le chat de la remplacer.`,
  },
  assumptionsToConfirm: { anInput: 'une donnée', assumedSuffix: (v) => ` — hypothèse retenue : ${v}` },
  editor: {
    enterNumber: 'Saisissez un nombre.',
    enterNumberFor: (l) => `Saisissez un nombre pour ${l}.`,
    enterPercentFor: (l) => `Saisissez un pourcentage pour ${l}.`,
    chooseYesNoFor: (l) => `Choisissez Oui ou Non pour ${l}.`,
    semicolonBetween: (l) => `Séparez les éléments de ${l} par « ; ».`,
    useNumbersFor: (l) => `Saisissez des nombres pour ${l}.`,
    wholeNumbersFor: (l) => `Saisissez des nombres entiers pour ${l}, séparés par « ; ».`,
    commaSeparated: 'séparés par des virgules',
  },
  freshnessUnknown: 'Nous n’avons pas pu vérifier si votre calcul enregistré intègre les dernières modifications de vos comptes. Actualisez la page et vérifiez vos comptes avant de vous fier à ces chiffres.',
  goals: { goalN: (n) => `Objectif ${n}`, whatMoves: 'ce qui le fait bouger →', earliest: (y) => `au plus tôt ≈ ${y}`, rank: 'Rang' },
  issues: {
    mainConstraint: 'La principale contrainte de ce plan',
    riskToReview: 'Risque à examiner',
    missingInformation: 'Information manquante',
    whyItMatters: (t) => `Pourquoi c’est important : ${t}`,
    seeEvidence: 'Voir les éléments justificatifs ↓',
    noneFound: 'L’analyse réalisée n’a relevé aucun point important à partir des informations actuellement présentes dans ce plan.',
    notAvailable: 'L’analyse des points d’attention n’est pas encore disponible. Une section vide ne signifie pas qu’il n’y a aucun point à traiter.',
    discussQuestion: 'Passez en revue les points relevés par ce plan. Expliquez ce qui les étaye et demandez-moi si une information manquante sur ma situation pourrait changer leur priorité ou leur conclusion.',
    discussLabel: 'Discuter de ces points',
  },
  policies: {
    optionTested: 'Option testée',
    computedEffect: (v) => `Effet calculé : ${v}`,
    tradeoff: (v) => `Arbitrage : ${v}`,
    backToAction: 'Retour à l’action ↑',
    tradeoffsHeading: 'Arbitrages issus des cas testés',
  },
  transfer: {
    toHeirs: (m) => `+${m} pour les héritiers`,
    fundedPoints: (label, pts) => `${label} ${pts} points de pourcentage`,
    noChange: ' · aucun changement pour le financement de vos objectifs',
  },
  tax: {
    rulesIntro: 'Seules les règles rattachées à ce calcul sont affichées. Chaque entrée présente les références de la source telles qu’elles ont été enregistrées ; les dates ou informations de champ d’application manquantes restent signalées comme manquantes.',
    title: 'Traitement fiscal appliqué dans ce plan',
    countryBookPrimary: 'La projection a appliqué, année après année, le barème fiscal du pays concerné. Les sources enregistrées expressément comme officielles ou primaires figurent ci-dessous.',
    countryBookNoPrimary: 'La projection a appliqué, année après année, le barème fiscal du pays concerné. Aucun lien vers une source primaire n’est encore joint à ce résultat enregistré : vérifiez les décisions à enjeu fiscal avant d’agir.',
    simplified: 'Le calcul a utilisé un traitement fiscal simplifié. Examinez les limites ci-dessous avant de vous fier à des comparaisons à enjeu fiscal.',
    askQuestion: 'Expliquez quelles lois fiscales et quels niveaux nationaux ont été appliqués à ce plan, lesquels ont été approchés ou exclus, et quels éléments de ma situation fiscale pourraient changer le résultat. N’utilisez que les sources primaires réellement rattachées à ce calcul et indiquez celles qui manquent.',
    askLabel: 'Poser une question sur le traitement fiscal',
  },
  inputs: { title: 'Données susceptibles de changer le résultat', infoToConfirm: 'Information à confirmer' },
  gated: {
    countChoices: (n, f) => (n === 1 ? `${f} option étudiée pour vous` : `${f} options étudiées pour vous`),
    mappedYears: (_n, f) => `nous avons projeté les ${f} prochaines années`,
    extraChecks: (n, f) => (n === 1 ? `${f} vérification supplémentaire de votre plan` : `${f} vérifications supplémentaires de votre plan`),
    placeholderRows: ['Ligne d’illustration', 'Chiffre d’exemple', 'Option d’exemple', 'Espace réservé'],
  },
}

export default fr
